"use client";

import { useRef } from "react";

import type {
 AuthAdapter, AuthCredentials, CallTransport, StorageAdapter,
} from "../context/execution-context";
import type { StateStore } from "../context/state-store";
import type { RuntimeLogger } from "../runtime/types";
import type { AtomContext } from "./atoms/registry-types";
import type { ResolvedApp, ResolvedScreen } from "./index-types";
import { usePrePaintEffect } from "./StudioScreenRenderer.pre-paint";
import type { NavigationFn } from "./StudioScreenRenderer.props";
import { refreshStaleSession } from "./StudioScreenRenderer.session";
import { isPlainObject } from "./StudioScreenRenderer.utils.data";
import { resolvePageLocale } from "./StudioScreenRenderer.utils.locale-route";
import {
 hasAuthenticatedSession, hasFreshAccessToken, isAuthGateRequired,
} from "./StudioScreenRenderer.utils.auth";

interface BootstrapOptions {
 bootstrapKey: string;
 screen: ResolvedScreen | null;
 app: ResolvedApp;
 navigate?: NavigationFn;
 storage?: StorageAdapter;
 auth?: AuthAdapter;
 store: StateStore;
 transport: CallTransport;
 logger: RuntimeLogger;
 fireTrigger: AtomContext["fireTrigger"];
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
 return (
 value !== null &&
 typeof value === "object" &&
 typeof (value as { then?: unknown }).then === "function"
 );
}

/**
 * Wait until the browser goes idle (or the deadline passes) so mount flows
 * do not compete with hydration and the first interactions.
 *
 * The deadline bounds the wait: on browsers without requestIdleCallback, or
 * under continuous main-thread pressure, the flow fires at most ~1.5s late
 * instead of never.
 */
const IDLE_TIMEOUT_MS = 1_500;

function waitForIdle(): Promise<void> {
 return new Promise((resolve) => {
 if (
 typeof window === "undefined" ||
 typeof window.requestIdleCallback !== "function"
 ) {
 setTimeout(resolve, 0);
 return;
 }

 const timeoutId = setTimeout(resolve, IDLE_TIMEOUT_MS);
 window.requestIdleCallback(
 () => {
 clearTimeout(timeoutId);
 resolve();
 },
 { timeout: IDLE_TIMEOUT_MS },
 );
 });
}

/**
 * Synchronously read the persisted interface language preference.
 *
 * Returns null when nothing valid is persisted (no storage, async adapter,
 * empty value, unsupported locale) so callers can distinguish "user never
 * chose a language" from "user chose<locale>".
 */
function readPersistedLanguageSync(
 storage: StorageAdapter | null,
 storageKey: string,
 supportedLocales: readonly string[],
): string | null {
 if (!storage) return null;

 let raw: string | null;
 try {
 const result = storage.read(storageKey);
 if (result !== null && isThenable(result)) return null;
 raw = result as string | null;
 } catch {
 return null;
 }
 if (typeof raw !== "string" || !raw.trim()) return null;

 let parsed: unknown;
 try {
 parsed = JSON.parse(raw);
 } catch {
 parsed = raw;
 }

 const candidate = isPlainObject(parsed) ? parsed.code : parsed;
 if (typeof candidate !== "string") return null;

 const code = candidate.trim();
 return supportedLocales.includes(code) ? code : null;
}

/**
 * The language preference is persisted under `<appSlug>.language`, matching
 * the same app-scoped convention as the session blob (`<appSlug>.session`)
 * and the flows that write it (e.g. "mental2.language").
 */
function resolveLanguageStorageKey(app: ResolvedApp): string {
 const slug = String(app.slug ?? "").trim();
 return slug ? `${slug}.language` : "mental2.language";
}

/**
 * Text direction for an app-configured locale.
 *
 * Shared by the bootstrap seeding and the per-route URL-locale sync in the
 * renderer component so both derive `dir` identically for the same locale.
 */
export function resolveAppLocaleDir(
 app: ResolvedApp,
 locale: string,
): "ltr" | "rtl" {
 return app.i18n?.dir?.[locale] ?? (locale === "ar" ? "rtl" : "ltr");
}

/**
 * Seed `state.language` / `state.dir` from the persisted preference BEFORE
 * the first paint. Without this, the UI rendered with the authored fallbacks
 * ("en"/"ltr") until the async mount flow completed, so a user with a saved
 * language saw the interface flash (or stick) in English.
 *
 * With locale-prefixed URLs, an explicit prefix (/en, /es, /ar, ...) IS the
 * source of truth for the page language and wins over the saved preference:
 * when the current path carries a supported locale, it seeds language/dir
 * directly (plus `state.urlLocale`, which flows use to detect an explicit
 * URL locale) and the persisted preference is left untouched. The lookup is
 * bundle-aware (resolvePageLocale) so a URL prefix the app config does not
 * list — but for which a translation bundle was loaded — still wins over the
 * persisted preference instead of silently restoring the saved language.
 */
function seedPersistedLanguage(
 app: ResolvedApp,
 storage: StorageAdapter | null,
 store: StateStore,
): void {
 const locales = app.i18n?.locales ?? ["en"];
 const urlLocale =
   typeof window !== "undefined"
     ? resolvePageLocale(
         window.location.pathname,
         locales,
         Object.keys(app.translationsByLocale ?? {}),
       )
     : undefined;
 if (urlLocale) {
   store.set("urlLocale", urlLocale);
   store.set("language", urlLocale);
   store.set("dir", resolveAppLocaleDir(app, urlLocale));
   return;
 }

 store.set("urlLocale", "");
 const code = readPersistedLanguageSync(
 storage,
 resolveLanguageStorageKey(app),
 locales,
 );
 if (!code) return;

 store.set("language", code);
 store.set("dir", resolveAppLocaleDir(app, code));
}

/**
 * Mount work runs pre-paint (layout effect) so the mount flow's optimistic
 * localStorage paint (e.g. `mental2.tasks.cache.v1.<status>`) is committed
 * before the browser composes the first frame — the placeholder never
 * becomes visible and the fresh API result only replaces the cache when the
 * network actually responds.
 *
 * Session refresh + seeding stay inside the pre-paint window (they are fast
 * and required for a correct first paint). The mount flows themselves
 * (`onMount` / `onLoad`) are deferred to browser idle time: their transport
 * work, store notifications, and re-renders otherwise compete directly with
 * hydration and drove the TBT/INP findings. The idle wait is deadline-bounded
 * so flows still fire promptly under main-thread pressure.
 *
 * The effect is keyed only by screen id (the caller passes a content-stable
 * bootstrap key), and it is guarded by a ref so the same bootstrap key never
 * runs twice within one renderer instance.
 *
 * The guard is RELEASED on cleanup whenever the run was interrupted before it
 * fired its triggers. React StrictMode intentionally invokes layout effects
 * as mount → cleanup → mount in development; without the release, the second
 * invocation saw the ref already holding the key and silently skipped the
 * mount flow even though the first run had been cancelled before reaching
 * `fireTrigger`. With the release, the cancelled run is retried exactly once
 * and completes — and a run that DID fire its triggers keeps its key, so
 * returning to an already-mounted screen still does not re-fire the mount
 * flow (its state is still loaded, and the flows' own duplicate-load guards
 * such as `portraitLoadedFilter`/`tasksLoadedStatus` stay intact).
 */
export function useScreenBootstrap(options: BootstrapOptions): void {
 const { bootstrapKey, screen, app, navigate, storage, auth, store, transport,
 logger, fireTrigger } = options;
 const ranBootstrapKeyRef = useRef<string | null>(null);

 usePrePaintEffect(() => {
 if (!bootstrapKey) return;
 if (ranBootstrapKeyRef.current === bootstrapKey) return;
 ranBootstrapKeyRef.current = bootstrapKey;
 let cancelled = false;
 let firedTriggers = false;

 seedPersistedLanguage(app, storage ?? null, store);

 void (async () => {
 let creds: AuthCredentials | null = null;
 try {
 creds = auth?.read() ?? null;
 } catch {
 creds = null;
 }
 store.set("session", creds ? { ...creds } : {});

 if (screen && isAuthGateRequired(screen) && !hasAuthenticatedSession(creds)) {
 const failureTarget = app.config.entrypoints?.onAuthFailure ?? "login";
 navigate?.({ kind: "screen", to: failureTarget, replace: true });
 firedTriggers = true;
 return;
 }

 // Only pay the network round-trip when the token is actually stale.
 // When it is fresh, `refreshStaleSession` was a no-op await that still
 // pushed the mount trigger (and therefore the cached snapshot paint)
 // behind an extra suspension. A stale token still refreshes BEFORE the
 // flows run so they never send an expired accessToken.
 if (creds && !hasFreshAccessToken(creds)) {
 try {
 await refreshStaleSession(creds, {
 transport, auth, store, isCancelled: () => cancelled,
 });
 } catch (err) {
 logger.warn("Session token refresh failed", err);
 }
 if (cancelled) return;
 }
 if (cancelled) return;

 if (screen?.logic?.["onMount"] || screen?.logic?.["onLoad"]) {
 await waitForIdle();
 if (cancelled) return;
 if (screen?.logic?.["onMount"]) {
 firedTriggers = true;
 fireTrigger("onMount");
 }
 if (screen?.logic?.["onLoad"]) {
 firedTriggers = true;
 fireTrigger("onLoad");
 }
 }
 })();

 return () => {
 cancelled = true;
 if (!firedTriggers) ranBootstrapKeyRef.current = null;
 };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [bootstrapKey]);
}